import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Camera, Check, Loader2, Sparkles, Trash2, X } from "lucide-react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config as env } from "../../Environment";

interface ExtractColumn {
  key: string;
  label: string;
  type?: string;
}

interface ImageExtractConfig {
  /** content.key of the smart_table on the same pane that receives the rows */
  targetTableKey?: string;
  /** Extra context for the AI (what the screenshots contain) */
  instructions?: string;
  maxImages?: number;
  /** Fallback columns when no target table is found */
  columns?: ExtractColumn[];
}

interface ImageExtractProps {
  config?: ImageExtractConfig;
  content: { key: string; label?: string };
  userData?: Record<string, any>;
  onChange?: (key: string, value: any) => void;
  readonly?: boolean;
  /** Injected by the runner: resolves the target table's column config */
  findTableColumns?: (tableKey: string) => ExtractColumn[];
  /** Activity language ("he"/"en"/"de") — wins over the app UI language */
  lang?: string;
}

/** Downscale + JPEG-compress an image file so uploads stay small */
const compressImage = (file: File, maxDim = 1600): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("canvas unavailable"));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not read image"));
    };
    img.src = url;
  });

/**
 * ImageExtract — upload bank-app screenshots / statement photos, let AI
 * extract the requested fields, review & fix the rows manually, then send
 * them into a linked smart_table on the same pane.
 */
const ImageExtract = ({
  config = {},
  content,
  userData,
  onChange,
  readonly = false,
  findTableColumns,
  lang,
}: ImageExtractProps) => {
  const { i18n } = useTranslation();
  const dir = lang ? (lang === "he" ? "rtl" : "ltr") : i18n.dir();
  const he = dir === "rtl";
  const retryFetch = useRetryFetch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Array<Record<string, string>> | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(
    Boolean(userData?.[content.key]?.confirmed)
  );

  const columns: ExtractColumn[] =
    (config.targetTableKey && findTableColumns?.(config.targetTableKey)) ||
    config.columns ||
    [];

  const maxImages = config.maxImages ?? 3;

  if (readonly) {
    const saved = userData?.[content.key];
    if (!saved?.confirmed) return null;
    return (
      <div dir={dir} className="my-2 text-xs text-muted-foreground">
        ✓ {he ? `נוספו ${saved.rowCount ?? 0} שורות מתוך ${saved.imageCount ?? 0} תמונות` : `Added ${saved.rowCount ?? 0} rows from ${saved.imageCount ?? 0} images`}
      </div>
    );
  }

  if (!columns.length) return null;

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    try {
      const compressed = await Promise.all(
        Array.from(files)
          .slice(0, maxImages - images.length)
          .map((f) => compressImage(f))
      );
      setImages((old) => [...old, ...compressed].slice(0, maxImages));
    } catch {
      setError(he ? "לא הצלחנו לקרוא את התמונה" : "Could not read the image");
    }
  };

  const extract = async () => {
    setExtracting(true);
    setError(null);
    try {
      const res = await retryFetch(`${env.API_BASE}/api/ai/extract-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          images,
          columns,
          instructions: config.instructions ?? "",
          language: lang ?? i18n.language,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? res.statusText);
      }
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (e: any) {
      setError(
        he
          ? "החילוץ נכשל — אפשר לנסות שוב או להקליד ידנית בטבלה"
          : "Extraction failed — try again or type into the table manually"
      );
    } finally {
      setExtracting(false);
    }
  };

  const setCell = (rowIdx: number, colKey: string, value: string) =>
    setRows((old) => (old ? old.map((r, i) => (i === rowIdx ? { ...r, [colKey]: value } : r)) : old));

  const deleteRow = (idx: number) =>
    setRows((old) => (old ? old.filter((_, i) => i !== idx) : old));

  const confirm = () => {
    if (!rows || !onChange) return;
    const nonEmpty = rows.filter((r) => columns.some((c) => String(r[c.key] ?? "").trim() !== ""));
    if (config.targetTableKey) {
      const existing = userData?.[config.targetTableKey];
      const existingRows = (existing?.rows ?? []).filter((r: Record<string, any>) =>
        columns.some((c) => String(r[c.key] ?? "").trim() !== "")
      );
      onChange(config.targetTableKey, {
        ...(existing ?? {}),
        rows: [...existingRows, ...nonEmpty],
        columns: columns.map(({ key, label, type }) => ({ key, label, type })),
        __injected: Date.now(),
      });
    }
    onChange(content.key, {
      confirmed: true,
      rowCount: nonEmpty.length,
      imageCount: images.length,
    });
    setConfirmed(true);
    setRows(null);
    setImages([]);
  };

  return (
    <div dir={dir} className="my-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
      <Label className="mb-1 flex items-center gap-1.5 text-sm font-medium">
        <Sparkles className="h-4 w-4 text-primary" />
        {content.label || (he ? "חילוץ אוטומטי מצילומי מסך" : "Auto-extract from screenshots")}
      </Label>
      <p className="mb-2 text-xs text-muted-foreground">
        {he
          ? "העלו צילומי מסך מאפליקציית הבנק / חברת האשראי או דפי חשבון — וה-AI ימלא את הטבלה בשבילכם. תמיד אפשר לתקן ידנית לפני האישור."
          : "Upload screenshots from your banking app or statements — AI fills the table for you. You can always fix values before confirming."}
      </p>

      {confirmed && !rows && (
        <div className="mb-2 flex items-center gap-1 text-xs text-green-700">
          <Check className="h-3.5 w-3.5" />
          {he ? "השורות נוספו לטבלה. אפשר להעלות עוד תמונות." : "Rows added to the table. You can upload more images."}
        </div>
      )}

      {/* image pickers / thumbnails */}
      <div className="flex flex-wrap items-center gap-2">
        {images.map((src, i) => (
          <div key={i} className="relative">
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img src={src} className="h-16 w-16 rounded-md border object-cover" />
            <button
              type="button"
              className="absolute -top-1.5 -end-1.5 rounded-full bg-background border p-0.5 text-muted-foreground hover:text-destructive"
              onClick={() => setImages((old) => old.filter((_, j) => j !== i))}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {images.length < maxImages && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="h-4 w-4 me-1" />
            {he ? "העלה צילום מסך" : "Upload screenshot"}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {images.length > 0 && !rows && (
          <Button type="button" size="sm" onClick={extract} disabled={extracting}>
            {extracting ? (
              <Loader2 className="h-4 w-4 me-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 me-1" />
            )}
            {extracting
              ? he ? "מחלץ נתונים…" : "Extracting…"
              : he ? "חלץ נתונים מהתמונות" : "Extract data"}
          </Button>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {/* review & fix extracted rows */}
      {rows && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-medium">
            {rows.length === 0
              ? he ? "לא זוהו פריטים בתמונות. אפשר לנסות תמונה ברורה יותר או להקליד ידנית." : "No items detected. Try a clearer image or type manually."
              : he ? `זוהו ${rows.length} שורות — בדקו ותקנו לפני האישור:` : `Detected ${rows.length} rows — review before confirming:`}
          </p>
          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border bg-background">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-muted/60">
                    {columns.map((c) => (
                      <th key={c.key} className="px-2 py-1.5 text-start text-xs font-semibold border-b">
                        {c.label}
                      </th>
                    ))}
                    <th className="w-8 border-b" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {columns.map((c) => (
                        <td key={c.key} className="border-b p-0">
                          <input
                            dir={c.type === "number" || c.type === "currency" ? "ltr" : dir}
                            className="w-full min-w-[6rem] bg-transparent px-2 py-1.5 outline-none focus:bg-primary/5"
                            value={row[c.key] ?? ""}
                            onChange={(e) => setCell(rowIdx, c.key, e.target.value)}
                          />
                        </td>
                      ))}
                      <td className="border-b text-center">
                        <button
                          type="button"
                          className="p-1 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteRow(rowIdx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 flex gap-2">
            {rows.length > 0 && (
              <Button type="button" size="sm" onClick={confirm}>
                <Check className="h-4 w-4 me-1" />
                {he ? "אשר והוסף לטבלה" : "Confirm & add to table"}
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => setRows(null)}>
              {he ? "בטל" : "Cancel"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageExtract;
